import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { parseDuration } from 'common/util/time';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ShowChart from '@mui/icons-material/ShowChart';
import BarChart from '@mui/icons-material/BarChart';
import TableChart from '@mui/icons-material/TableChart';
import PieChart from '@mui/icons-material/PieChart';
import AreaChartIcon from 'components/common/Graphs/AreaChartIcon';
import { UrlParams } from 'common/types';
import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import GraphStyleMenu, { GraphStyleSection } from 'components/common/Graphs/GraphStyleDropdown/GraphStyleMenu';
import LineSettingsMenu from 'components/common/Graphs/GraphStyleDropdown/LineSettingsMenu';
import BarSettingsMenu from 'components/common/Graphs/GraphStyleDropdown/BarSettingsMenu';
import PieSettingsMenu from 'components/common/Graphs/GraphStyleDropdown/PieSettingsMenu';
import { queryTimeRangeState, routeState, searchParamState } from 'components/common/Managers/RouteStateManager.state';

export type BarWidth = {
  value: string;
  label: string;
};

const AUTO_BAR_WIDTH = { value: 'auto', label: 'Auto' };

const BAR_WIDTH_OPTIONS = [
  AUTO_BAR_WIDTH,
  { value: '1 minute', label: '1 minute' },
  { value: '5 minutes', label: '5 minutes' },
  { value: '10 minutes', label: '10 minutes' },
  { value: '15 minutes', label: '15 minutes' },
  { value: '30 minutes', label: '30 minutes' },
  { value: '1 hour', label: '1 hour' },
  { value: '6 hours', label: '6 hours' },
  { value: '12 hours', label: '12 hours' },
  { value: '1 day', label: '1 day' },
  { value: '2 days', label: '2 days' },
] as Array<BarWidth>;

const ALL_GRAPH_STYLE_PARAMS = [
  K.URL_PARAM.GRAPH_STYLE,
  K.URL_PARAM.LINE_SMOOTHING,
  K.URL_PARAM.Y_SCALE,
  K.URL_PARAM.BAR_WIDTH,
  K.URL_PARAM.PIE_STYLE,
  K.URL_PARAM.MAX_PIE_SLICES,
];

const DEFAULT_BAR_CHART_SLICES = 40;

// TODO: This helper function to determine valid bar width logic is mostly ported from the existing code in {@link ChartTypeDropdown.tsx}.
// Will likely make sense to refactor into Graphs.ts and add unit tests.
const isValidBarWidthOption = (bw: BarWidth, startTime: string, endTime: string): boolean => {
  if (bw.value === K.AUTO_BAR_WIDTH) {
    return true;
  }
  const duration = Number(endTime) - Number(startTime);
  const barWidthDuration = parseDuration(bw?.value);
  const slices = duration / barWidthDuration;
  return slices < K.DEFAULT_AUTO_SEARCH_GRAPH_SLICES && slices >= 1; // we might want to get smarter about this in the future, based on viewport size, etc.
};

type GraphStyleDropdownProps = {
  className?: string;
  buttonVariant?: ButtonVariant;
  buttonSize?: 'sm' | 'lg' | undefined;
};

const lineChartIcon = <ShowChart fontSize="small" />;
const areaChartIcon = <AreaChartIcon fontSize="small" />;
const barChartIcon = <BarChart fontSize="small" />;
const tableChartIcon = <TableChart fontSize="small" />;
const pieChartIcon = <PieChart fontSize="small" />;

/**
 * This component allows the user to select a graph style (i.e. "Bar", "Line", "Area", etc.)
 * Selection updates the GRAPH_STYLE url param
 */
function GraphStyleDropdown({ className, buttonVariant, buttonSize }: GraphStyleDropdownProps): JSX.Element {
  const route = useRecoilValue(routeState);
  const graphStyleParam = useRecoilValue(searchParamState(K.URL_PARAM.GRAPH_STYLE));
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const barWidthParam = useRecoilValue(searchParamState(K.URL_PARAM.BAR_WIDTH));
  const yScaleParam = useRecoilValue(searchParamState(K.URL_PARAM.Y_SCALE));
  const numBarsParam = useRecoilValue(searchParamState(K.URL_PARAM.NUM_BARS));
  const lineSmoothingParam = useRecoilValue(searchParamState(K.URL_PARAM.LINE_SMOOTHING));

  const pieStyleParam = useRecoilValue(searchParamState(K.URL_PARAM.PIE_STYLE));
  const slicesParam = useRecoilValue(searchParamState(K.URL_PARAM.MAX_PIE_SLICES));
  const validBarWidthOptions = BAR_WIDTH_OPTIONS.filter((option) => isValidBarWidthOption(option, startTime, endTime));
  const currentBarWidth = validBarWidthOptions.find((option) => option.value === barWidthParam) || AUTO_BAR_WIDTH;
  const [barWidth, setBarWidth] = useState<BarWidth>(currentBarWidth);
  const [yScale, setYScale] = useState(yScaleParam || K.Y_SCALE_OPTIONS.LINEAR);
  const [numBars, setNumBars] = useState(Number(numBarsParam) || DEFAULT_BAR_CHART_SLICES);
  const [lineSmoothing, setLineSmoothing] = useState(lineSmoothingParam || K.LINE_OPTIONS.STRAIGHT_LINES);
  const [pieStyle, setPieStyle] = useState(pieStyleParam || K.PIE_CHART_TYPES.PIE);
  const [maxPieSlices, setMaxPieSlices] = useState(Number(slicesParam || K.DEFAULT_MAX_PIE_SLICES));
  const [showOverlay, setShowOverlay] = useState(false);
  const [isNumBars, setIsNumBars] = useState(!numBarsParam);
  const setSearchParams = useSetSearchParams();

  // List of graph style sections and associated settings menus
  let sections: Array<GraphStyleSection> = [
    {
      style: K.CHART_TYPES.LINE,
      settings: (
        <LineSettingsMenu
          lineSmoothing={lineSmoothing}
          setLineSmoothing={setLineSmoothing}
          yScale={yScale}
          setYScale={setYScale}
          barWidth={barWidth}
          setBarWidth={setBarWidth}
          barWidthOptions={validBarWidthOptions}
        />
      ),
      itemContent: (
        <>
          {lineChartIcon}
          {'Line'}
        </>
      ),
      testId: ELEMENT.graphStyleDropdownLine,
    },
    {
      style: K.CHART_TYPES.STACKED,
      settings: (
        <LineSettingsMenu
          lineSmoothing={lineSmoothing}
          setLineSmoothing={setLineSmoothing}
          yScale={yScale}
          setYScale={setYScale}
          barWidth={barWidth}
          setBarWidth={setBarWidth}
          barWidthOptions={validBarWidthOptions}
        />
      ),
      itemContent: (
        <>
          {areaChartIcon}
          {'Area'}
        </>
      ),
      testId: ELEMENT.graphStyleDropdownArea,
    },
    {
      style: K.CHART_TYPES.STACKED_BAR,
      settings: (
        <BarSettingsMenu
          yScale={yScale}
          setYScale={setYScale}
          barWidth={barWidth}
          numBars={numBars}
          isNumBars={isNumBars}
          setBarWidth={setBarWidth}
          setNumBars={setNumBars}
          setIsNumBars={setIsNumBars}
          barWidthOptions={validBarWidthOptions}
        />
      ),
      itemContent: (
        <>
          {barChartIcon}
          {'Bar/Column'}
        </>
      ),
      testId: ELEMENT.graphStyleDropdownBar,
    },
  ];

  // Different list of sections / settings for PQ graphs.
  if (route === K.ROUTE.POWER_QUERY) {
    sections = [
      {
        style: K.PQ_DATA_VIS_TYPES.TABLE,
        itemContent: (
          <>
            {tableChartIcon}
            {'Table'}
          </>
        ),
        testId: ELEMENT.graphStyleDropdownTable,
      },
      {
        style: K.CHART_TYPES.LINE,
        settings: (
          <LineSettingsMenu
            lineSmoothing={lineSmoothing}
            setLineSmoothing={setLineSmoothing}
            yScale={yScale}
            setYScale={setYScale}
          />
        ),
        itemContent: (
          <>
            {lineChartIcon}
            {'Line'}
          </>
        ),
        testId: ELEMENT.graphStyleDropdownLine,
      },
      {
        style: K.CHART_TYPES.STACKED_BAR,
        settings: <BarSettingsMenu yScale={yScale} setYScale={setYScale} />,
        itemContent: (
          <>
            {barChartIcon}
            {'Bar/Column'}
          </>
        ),
        testId: ELEMENT.graphStyleDropdownBar,
      },
      {
        style: K.CHART_TYPES.PIE,
        settings: (
          <PieSettingsMenu
            pieStyle={pieStyle}
            setPieStyle={setPieStyle}
            maxPieSlices={maxPieSlices}
            setMaxPieSlices={setMaxPieSlices}
          />
        ),
        itemContent: (
          <>
            {pieChartIcon}
            {'Pie'}
          </>
        ),
        testId: ELEMENT.graphStyleDropdownPie,
      },
    ];
  }

  // Construct UrlParam object based on what graphStyle was selected.
  const getParams = (graphStyle: string): UrlParams => {
    const params = {} as UrlParams;
    // Clear out any stale params from previous graph styles.
    ALL_GRAPH_STYLE_PARAMS.forEach((param) => (params[param] = ''));
    switch (graphStyle) {
      case K.CHART_TYPES.LINE:
        return {
          ...params,
          graphStyle,
          lineSmoothing,
          yScale,
          barWidth: barWidth.value,
        } as UrlParams;
      case K.CHART_TYPES.STACKED:
        return {
          ...params,
          graphStyle,
          lineSmoothing,
          yScale,
          barWidth: barWidth.value,
        } as UrlParams;
      case K.CHART_TYPES.STACKED_BAR:
        return {
          ...params,
          graphStyle,
          yScale,
          barWidth: isNumBars ? barWidth.value : '',
          numBars: isNumBars ? '' : String(numBars),
        } as UrlParams;
      case K.CHART_TYPES.PIE:
        return {
          ...params,
          graphStyle,
          pieStyle,
          maxPieSlices: String(maxPieSlices),
        } as UrlParams;
      case K.PQ_DATA_VIS_TYPES.TABLE:
        return {
          ...params,
        } as UrlParams;
      default:
        return {
          ...params,
          graphStyle: K.CHART_TYPES.LINE,
        } as UrlParams;
    }
  };

  const onSubmit = (graphStyle: string) => {
    const params = getParams(graphStyle);
    setSearchParams(params);
    setShowOverlay(false);
  };

  const getButtonIcon = (): JSX.Element => {
    switch (graphStyleParam) {
      case K.CHART_TYPES.LINE:
        return lineChartIcon;
      case K.CHART_TYPES.STACKED:
        return areaChartIcon;
      case K.CHART_TYPES.STACKED_BAR:
        return barChartIcon;
      case K.CHART_TYPES.PIE:
        return pieChartIcon;
      default:
        return route !== K.ROUTE.POWER_QUERY ? lineChartIcon : tableChartIcon;
    }
  };

  const getButtonText = (): string => {
    switch (graphStyleParam) {
      case K.CHART_TYPES.LINE:
        return 'Line Chart';
      case K.CHART_TYPES.STACKED: // "STACKED" legacy area param from angular
        return 'Area Chart';
      case K.CHART_TYPES.STACKED_BAR:
        return 'Bar / Column Chart';
      case K.CHART_TYPES.PIE:
        return 'Pie Chart';
      default:
        return route === K.ROUTE.POWER_QUERY ? 'Table' : 'Line Chart';
    }
  };

  const buttonIcon = getButtonIcon();
  const buttonText = getButtonText();
  const overlay = (
    <GraphStyleMenu route={route} graphStyleParam={graphStyleParam} sections={sections} onSubmit={onSubmit} />
  );

  return (
    <OverlayTrigger placement="bottom-end" overlay={overlay} showOverlay={showOverlay} setShowOverlay={setShowOverlay}>
      <Button
        className={className}
        variant={buttonVariant}
        leftIcon={buttonIcon}
        rightIcon={<ArrowDropDownIcon fontSize="small" />}
        size={buttonSize}
        testId={ELEMENT.graphStyleDropdown}
      >
        {buttonText}
      </Button>
    </OverlayTrigger>
  );
}

export default GraphStyleDropdown;
